import { useTranslation } from 'react-i18next';
import { type BaseSyntheticEvent, useCallback, useState } from 'react';
import { useUpdateProductPolicies } from '@travelity/api/services';
import { useForm } from '@travelity/form/use-form';
import { PolicyType } from '@travelity/api/generated';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
} from '@mui/material';
import { LoadingOverlay } from '@travelity/ui/loading-overlay';
import { RichTextArea } from '@travelity/form/controls';
import AccordionButtons from '@/views/orders/components/accordion-buttons';
import { useToast } from '@/hooks';
import type { PackageSidebarItemsProps } from './types';

export const PackagePolicies = ({ product }: PackageSidebarItemsProps) => {
    const { t } = useTranslation('product');
    const { showSuccess } = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const { mutate: updatePolicies, isLoading: isUpdatingPolicies } =
        useUpdateProductPolicies();

    const {
        Form: PoliciesForm,
        reset: resetPolicies,
        formState: { errors: policiesErrors },
        watch,
    } = useForm({
        defaultValues: {
            policies: [],
            policyText: '',
        },
        onSubmit: useCallback(
            (data: { policyText: string }, e?: BaseSyntheticEvent) => {
                e?.preventDefault();
                updatePolicies(
                    {
                        productId: product.id,
                        data: {
                            items: [
                                {
                                    type: PolicyType.PRODUCT_CANCELLATION,
                                    enabled: true,
                                    description: data.policyText,
                                },
                            ],
                        },
                    },
                    {
                        onSuccess: () => {
                            setIsEditing(false);
                            showSuccess('UPDATE_PRODUCT_POLICIES_SUCCESS', {
                                name: product.name,
                            });
                        },
                    }
                );
            },
            [product.id, product.name, showSuccess, updatePolicies]
        ),
        mode: 'onChange',
    });

    const onCancel = () => {
        resetPolicies();
        setIsEditing(false);
    };

    return (
        <PoliciesForm id="policies">
            <Accordion
                disableGutters
                expanded={isExpanded}
                onChange={() => !isEditing && setIsExpanded(!isExpanded)}
            >
                <AccordionSummary>
                    {t('Product Policies')}
                    {isExpanded && (
                        <AccordionButtons
                            editing={isEditing}
                            saving={isUpdatingPolicies}
                            errors={policiesErrors}
                            onEditCancel={onCancel}
                            onEdit={() => setIsEditing(true)}
                        />
                    )}
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ width: '100%' }}>
                        {!isEditing ? (
                            <Box sx={{ width: '100%' }}>
                                {!watch('policyText') &&
                                    t('No policies defined')}
                                {watch('policyText') && (
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: watch('policyText'),
                                        }}
                                    />
                                )}
                            </Box>
                        ) : (
                            <RichTextArea
                                placeholder={t('Type Cancellation Policy')}
                                name="policyText"
                                showErrors={!!policiesErrors}
                                hideHelperText
                                label={t('Cancellation Policy')}
                                required
                                sx={{
                                    width: '100%',
                                    minHeight: '200px',
                                }}
                            />
                        )}
                    </Box>
                    {isUpdatingPolicies && <LoadingOverlay />}
                </AccordionDetails>
            </Accordion>
        </PoliciesForm>
    );
};
